import React, { useRef, useState } from "react";
import { Modal, message, Form, Input } from "antd";
import { saveAs } from "file-saver";
import { useParams } from "react-router-dom";
import { MindMapContext } from "../../Context/contex";
import useMindMapService from "../../hooks/useMindMapService";

const FILE_EXTENSION = ".mindmap";
const FILE_NAME_PATTERN = /^[^<>:;,?"*|/\\]+$/;
const DEFAULT_FILE_NAME = "Untitled document";
const JSON_INDENT = 2;

const withoutDbFields = ({ __v, _id, ...rest }) => rest;

/**
 * Import a `.mindmap` file over the current map, or export the map as one.
 * The caller renders `holder` (the hidden file input and the file-name dialog),
 * like antd's `Modal.useModal`, so every trigger shares the same flow.
 */
export const useImportExport = () => {
  const { documentId } = useParams();
  const { documentInfo, nodes, edges, setNodes, setEdges, clearHistory } = MindMapContext();
  const mindMapService = useMindMapService();
  const fileInputRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileName, setFileName] = useState("");
  const [form] = Form.useForm();

  const startImport = () => fileInputRef.current.click();

  const startExport = () => {
    form.setFieldsValue({ fileName: documentInfo?.name || DEFAULT_FILE_NAME });
    setFileName(documentInfo?.name || "");
    setIsModalVisible(true);
  };

  const handleExport = () => {
    form
      .validateFields()
      .then(() => {
        try {
          const content = { nodes: nodes.map(withoutDbFields), edges: edges.map(withoutDbFields) };
          const blob = new Blob([JSON.stringify(content, null, JSON_INDENT)], { type: "application/json" });
          saveAs(blob, `${fileName}${FILE_EXTENSION}`);
          message.success(`File successfully exported as ${fileName}${FILE_EXTENSION}`);
          setIsModalVisible(false);
        } catch (exportError) {
          console.error("Export failed:", exportError);
          message.error("An error occurred during export. Please try again.");
        }
      })
      .catch((validationError) => {
        console.error("Validation failed:", validationError);
        message.error("Please enter a valid file name.");
      });
  };

  const handleImport = async (event) => {
    try {
      const file = event.target.files[0];
      if (!file) return;
      const importedMap = JSON.parse(await file.text());
      if (!importedMap) return;
      Modal.confirm({
        title: "Confirm",
        content: "Do you want to overwrite?",
        okText: "Yes",
        cancelText: "No",
        onOk: () => {
          const { nodes: importedNodes, edges: importedEdges } = importedMap;
          importedNodes && setNodes([...importedNodes]);
          importedEdges && setEdges([...importedEdges]);
          // An import replaces the whole map, so earlier steps cannot be undone onto it (D-036).
          clearHistory();
          mindMapService.saveImportDataToDb(
            { nodes: importedNodes, edges: importedEdges, documentId },
            { nodes: importedNodes, edges: importedEdges }
          );
          message.success("Mindmap imported successfully!");
        },
      });
    } catch (error) {
      message.error("Error importing slides. Please check the file format.");
      console.error("Error importing slides:", error);
    } finally {
      event.target.value = "";
    }
  };

  const holder = (
    <>
      <input type="file" accept={FILE_EXTENSION} hidden ref={fileInputRef} onChange={handleImport} />
      <Modal
        title="Set File Name"
        open={isModalVisible}
        onOk={handleExport}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form}>
          <Form.Item
            label="File Name"
            name="fileName"
            rules={[
              { required: true, message: "Please enter a file name." },
              { pattern: FILE_NAME_PATTERN, message: "File name contains invalid characters." },
            ]}
          >
            <Input value={fileName} onChange={(e) => setFileName(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

  return { startImport, startExport, holder };
};
